/**
 * AmitySocialHub - A component for displaying social hub
 *
 * @module views/components/AmitySocialHub
 * @memberof -Common
 */
import './AmitySocialHub.scss';
import '@amityco/ulta-ui-kit/dist/index.css';

import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import LoaderCircular from '@ulta/core/components/LoaderCircular/LoaderCircular';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { getStorage, setStorage } from '@ulta/core/utils/storage/storage';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys';

import * as utils from './AmitySocialHub';

/**
 * Represents a AmitySocialHub component
 *
 * @method
 * @param {AmitySocialHubProps} props - React properties passed from composition
 * @returns AmitySocialHub
 */
export const AmitySocialHub = ( props ) => {
  const [AmityKit, setAmityKit] = useState( null );
  const { profileId, loginAction, invokeAction, fetchAuthTokenAction, authToken, authTokenRequest, community, user, post } = props;
  const [hasLoaded, setHasLoaded] = useState( false );
  const [dailyPrompt, setDailyPrompt] = useState( false );
  const [shouldShowLogin, setShouldShowLogin] = useState( false );
  const [authTokenProfileId, setAuthTokenProfileId] = useState( null );
  const [fetchAuthToken, setFetchAuthToken] = useState( false );
  const authTokenRef = useRef( authToken );

  useEffect( () => {
    authTokenRef.current = authToken;
  }, [authToken] );

  useEffect( () => {
    utils.handleLoginPrompt( { hasLoaded, loginAction, shouldShowLogin }, { invokeAction, setShouldShowLogin } );
  }, [hasLoaded, loginAction, shouldShowLogin] );

  useEffect( () => {
    utils.handleDailyPrompt( { setDailyPrompt, setShouldShowLogin } );
  }, [dailyPrompt] );


  useEffect( () => {
    utils.handleAuthTokenRequest( { authToken, fetchAuthToken, fetchAuthTokenAction, authTokenProfileId },
      { invokeAction } );
  }, [fetchAuthToken, invokeAction] );

  useEffect( () => {
    utils.handleAuthTokenResponse( { authToken, authTokenRequest, fetchAuthToken }, { setFetchAuthToken } );
  }, [authTokenRequest, authToken] );

  const {
    amityClientKey,
    amityApiEndpoint,
    amityMessageOrigin,
    faqCommunityId,
    newsCommunityId,
    termsAndConditionsUrl,
    privacyAndPolicyUrl,
    allowUpdateDisplayName,
    defaultTab
  } = useAppConfigContext();

  const getAuthToken = () => {
    const fetchAuthTokenPromise = new Promise( ( resolve ) => {
      const checkForAuthToken = () => {
        if( authTokenRef.current ){
          const token = authTokenRef.current;
          // reset the ref
          authTokenRef.current = null;
          resolve( token );
        }
        else {
          setTimeout( checkForAuthToken, 200 );
        }
      };
      checkForAuthToken();
    } );

    return fetchAuthTokenPromise
      .then( token => token )
      .catch( error => {
        return '';
      } );
  };

  const ultaAmityConfig = {
    faqCommunityId,
    newsCommunityId,
    termsAndConditionsUrl,
    privacyAndPolicyUrl,
    allowUpdateDisplayName,
    defaultTab,
    deeplink: {
      origin: `${amityMessageOrigin}/community`,
      groupQueryString: 'community',
      postQueryString: 'post',
      userQueryString: 'user'
    }
  };
  useEffect( () => {
    // Dynamically import the UI kit to prevent SSR errors.
    import( '@amityco/ulta-ui-kit' ).then( module => {
      setAmityKit( module );
    } );

    const handleIframeMessage = ( event ) => {
      // Handles anonymous user login prompts
      if( event.data === 'explorePageLoaded' || event.data === 'onPageLoaded' ){
        setDailyPrompt( true );
        setHasLoaded( true );
        // initialize default it back to the original
      }
      if( event.data === 'anonymousNeedsLogin' ){
        setShouldShowLogin( true );
      }

      // Handles scrolling the parent window to the top
      if( event.data === 'parentNeedsScrollToTop' ){
        window.scrollTo( 0, 0 );
      }

    };

    window.addEventListener( 'message', handleIframeMessage );

    return () => {
      window.removeEventListener( 'message', handleIframeMessage );
    };
  }, [] );

  if( !AmityKit || !profileId || !amityClientKey || !amityApiEndpoint ){
    return (
      <div className='AmitySocialHub' style={ { position: 'relative', minHeight: '100dvh' } }>
        <LoaderCircular />
      </div>
    );
  }

  // The Amity UI Kit components are now loaded and can be used safely.
  const { AmityUiKitProvider, AmityUiKitSocial } = AmityKit;

  return (
    <div className='AmitySocialHub'>
      { profileId &&
      <AmityUiKitProvider
        key={ profileId }
        apiKey={ amityClientKey }
        apiEndpoint={ amityApiEndpoint }
        userId={ profileId }
        apiRegion='us'
        ultaConfig={ ultaAmityConfig }
        getAuthToken={ getAuthToken }
      >
        <AmityUiKitSocial />
      </AmityUiKitProvider> }
    </div>
  );
};

/**
 * Handles the authtoken response from DXL...triggers event that sends the authtoken back to the child iframe
 * @param {object} data - Arguments
 * @param {object} methods - Methods
 */
export const handleAuthTokenResponse = ( data, methods ) => {
  const { authToken, fetchAuthToken } = data || {};
  const { setFetchAuthToken } = methods || {};

  // no need to process since auth token never requested
  if( !fetchAuthToken ){
    return;
  }

  setFetchAuthToken( false );
};


/**
 * handles the authToken request from the child iframe....initiates a DXL action that calls the AmitySocialHub query
 * which returns the latest authToken based on the profileId passed in
 * @param data
 * @param methods
 */
export const handleAuthTokenRequest = ( data, methods ) => {
  const { fetchAuthToken, authTokenProfileId, fetchAuthTokenAction } = data || {};
  const { invokeAction } = methods || {};

  if( !fetchAuthToken ){
    return;
  }

  if( fetchAuthToken ){
    // get another token for the specific profile id
    const authTokenLoginActionWithProfileId = { ...fetchAuthTokenAction,
      variables: { moduleParams: { testKey: Math.random(), profileId: authTokenProfileId, loginStatus: 'anonymous', authTokenRequest: new Date().getTime() } }
    };
    invokeAction( authTokenLoginActionWithProfileId );
  }
};

/**
 * Handles login prompt behavior
 * @param {object} data - Arguments
 * @param {object} methods - Methods
 */
export const handleLoginPrompt = ( data, methods ) => {
  const { hasLoaded, loginAction, shouldShowLogin } = data || {};
  const { invokeAction, setShouldShowLogin } = methods || {};

  if( !hasLoaded || !loginAction || !shouldShowLogin ){
    return;
  }
  const fakeloginAction = { ...loginAction,
    variables: { moduleParams: { testKey: Math.random() } }
  };
  invokeAction( fakeloginAction );
  setShouldShowLogin( false );
};

/**
 * Handles daily prompt timer
 * @param {object} methods - Methods
 */
export const handleDailyPrompt = ( methods ) => {

  const { setDailyPrompt, setShouldShowLogin } = methods || {};

  let lastAmityPrompt = getStorage( { secure: false, key: STORAGE_KEY.amityPrompt } ) || {};

  if( !lastAmityPrompt?.lastPrompt || ( Date.now() - lastAmityPrompt.lastPrompt > 86400000 ) ){
    lastAmityPrompt = Date.now();
    const lastPromptData = { lastPrompt : lastAmityPrompt };
    setStorage( {
      secure: false,
      key: STORAGE_KEY.amityPrompt,
      value: lastPromptData
    } );
    setDailyPrompt( false );
    setShouldShowLogin( true );
  }
};

/**
 * Property type definitions
 * @typedef AmitySocialHubProps
 * @type {object}
 * @property {string} profileId - The user's profile ID.
 * @property {object} loginAction - Action to invoke for login.
 * @property {function} invokeAction - Function to invoke actions.
 * @property {object} fetchAuthTokenAction - Action to fetch an auth token.
 * @property {string} authToken - The current auth token.
 * @property {number} authTokenRequest - Timestamp of the last token request.
 */
AmitySocialHub.propTypes = {
  profileId: PropTypes.string.isRequired,
  loginAction: PropTypes.object,
  invokeAction: PropTypes.func,
  fetchAuthTokenAction: PropTypes.object,
  authToken: PropTypes.string,
  authTokenRequest: PropTypes.number
};

export default AmitySocialHub;
